import styles from "./styles.module.scss";
import { FC, useEffect, useState, useContext } from "react";
import { Props } from "./typings";
import CloseIcon from "assets/icons/close.svg?react";
import KeysContext from "contexts/KeysContext";
import { ItemKind } from "dto/IKindItems";
import CPluginContext from "contexts/PluginContext";
import Spinner from "components/Spinner";
import usePDF from "hooks/usePDF";
import PluginNotExist from "components/PluginNotExist";
import PDFViewer from "components/PDFViewer";
import PackagesContext from "contexts/PackagesContext";
import DossierFilesContext from "contexts/DossierFilesContext";
import { replaceFileExtension } from "utils/replaceFileExtension";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import PackageThreadContext from "contexts/PackageThreadContext";
import ModalsManagerContext, {
  InvoiceRegistryModalOptions,
  ContractRegistryModalOptions,
} from "contexts/ModalsManager";
import { useTranslation } from "react-i18next";

const PreviewModal: FC<Props> = ({
  itemId,
  itemKind,
  fileUri,
  fileName,
  attachmentId,
  encryptionKeyId,
  toggle,
}) => {
  const { t } = useTranslation();
  const { packages } = useContext(PackagesContext);
  const { threadPackages } =
    useContext(PackageThreadContext);
  const { dossierFiles } = useContext(DossierFilesContext);

  useContext(KeysContext);
  useContext(CPluginContext);
  const [showUpdatePlugin] = useState<boolean>(false);
  const {
    blob,
    isLoading,
    showInstallPlugin,
    setShowInstallPlugin,
    create,
  } = usePDF();

  const handleOpenInNewTab = () => {
    window.open(
      `${
        import.meta.env.VITE_APP_SITE ?? "https://alfa.pocztaprawnicza.pl"
      }/document?pdf=${itemId}:${fileName}:${
        fileUri ?? attachmentId
      }:${itemKind}:${encryptionKeyId}`,
    );
  };

  // „Dodaj do rejestru" z paska podglądu: rejestruje podglądany plik jako fakturę
  // (lub umowę) — otwiera formularz z dołączonym plikiem (tryb preloadedFile:
  // sekcja „Dodaj plik" ukryta, pola puste, wybór typu po stronie użytkownika).
  const { modals, setModals, setOptions } = useContext(ModalsManagerContext);

  const handleAddToRegistry = (type: "contracts" | "invoices") => {
    if (!blob) return;
    const name = fileName ?? "dokument.pdf";
    if (type === "contracts") {
      const opts = new ContractRegistryModalOptions();
      opts.file = blob;
      opts.fileName = name;
      setOptions(opts);
      setModals({ ...modals, createContractEntry: true, hide: false });
    } else {
      const opts = new InvoiceRegistryModalOptions();
      opts.file = blob;
      opts.fileName = name;
      setOptions(opts);
      setModals({ ...modals, createInvoiceRegistryEntry: true, hide: false });
    }
    toggle(false);
  };

  useEffect(() => {
    let attach = packages
      .reduce((a, v) => a.concat(v.Attachments ?? []), [])
      .find(
        (attachment) =>
          attachment.Uri === fileUri ||
          attachment.Id === fileUri ||
          attachment.Id === itemId,
      );

    if (!attach) {
      attach = threadPackages
        .reduce((a, v) => a.concat(v.Attachments ?? []), [])
        .find(
          (attachment) =>
            attachment.Uri === fileUri ||
            attachment.Id === fileUri ||
            attachment.Id === itemId,
        );
    }

    console.log("PreviewModal", fileUri, itemId, attach, threadPackages);

    if (attach) {
      create(fileUri, attach, Number(itemKind) as ItemKind);
    } else {
      const di = dossierFiles.find(
        (d) => d.FileUri == fileUri || d.SignedPdfUri == fileUri,
      );

      if (di) {
        create(
          di.AttachmentParentId ?? fileUri,
          {
            Id: di.DossierId,
            Uri: di.SignedPdfUri ? di.SignedPdfUri : di.FileUri,
            FileName: di.SignedPdfUri
              ? replaceFileExtension(di.FileName, ".pdf")
              : di.FileName,
            ContentType: di.FileContentType,
            Format: di.FileContentType,
            Kind: Number(itemKind),
            Label: di.Label,
            Size: di.FileSize,
            CommentsCount: di.CommentsCount,
            CreationDate: di.CreationDate,
            DeskInfo_IsOn: false,
            DossierId: di.DossierId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: di.LastModified,
            IsShared: di.IsShared,
            TagCount: di.TagCount,
            EncryptionKeyId: encryptionKeyId,
            Keys: [],
            ObjectKeys: [],
          },
          Number(itemKind) as ItemKind,
        );
      } else if (
        itemKind === ItemKind.CourtPortalDeliverance ||
        itemKind === ItemKind.KSeFInvoice
      ) {
        console.log("Create for itemKind", itemKind);
        create(
          itemId,
          {
            Id: itemId,
            Uri: null,
            FileName: fileName,
            ContentType: "application/pdf",
            Format: ".pdf",
            Kind: itemKind,
            Label: "",
            Size: 0,
            CommentsCount: 0,
            CreationDate: null,
            DeskInfo_IsOn: false,
            DossierId: itemId,
            ShowInDossier: 0,
            Type: DossierItemType.Attachment,
            LastModified: null,
            IsShared: false,
            TagCount: 0,
            EncryptionKeyId: encryptionKeyId,
            Keys: [],
            ObjectKeys: [],
          },
          itemKind,
        );
      }
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      {isLoading && (
        <div className={styles.title}>
          {t("common.preview")}
          <CloseIcon
            onClick={() => toggle(false)}
            className={styles.closeIcon}
          />
        </div>
      )}
      <div className={styles.content}>
        {isLoading && (
          <div className={styles.loading}>
            <Spinner></Spinner>
          </div>
        )}
        {!isLoading && blob && !showUpdatePlugin && (
          <PDFViewer
            className={styles.viewer}
            file={blob}
            sourceFile={blob}
            showHeader={true}
            headerClassName={styles.title}
            title={t("common.preview")}
            onCloseButton={() => toggle(false)}
            withoutBorder={true}
            inModal={true}
            handleOpenInNewTab={handleOpenInNewTab}
            handleAddToRegistry={handleAddToRegistry}
            fileName={fileName}
          />
        )}
        {!isLoading && showUpdatePlugin && (
          <PDFViewer
            className={styles.viewer}
            file={new Blob([])}
            showHeader={true}
            headerClassName={styles.title}
            showVersion={true}
            showTitle={true}
            title={t("common.preview")}
            onCloseButton={() => toggle(false)}
            withoutBorder={true}
            inModal={true}
            onlyPDFButtons={false}
            fileName={fileName}
          />
        )}
        {showInstallPlugin && (
          <PluginNotExist
            message={t("previewModal.installPluginMessage")}
            handleClose={() => {
              setShowInstallPlugin(false);
              toggle(false);
            }}
          ></PluginNotExist>
        )}
      </div>
    </div>
  );
};

export default PreviewModal;
